<template>
  <div class="landing">
    <div class="landing__content">
      <page-header :header-class="'header--landing'">
        <h1 v-if="!clickedNext" class="landing-header__title">
          {{ $t("landingPage.greetings.title") }}
          <br />
          {{ $t("landingPage.greetings.subTitle") }}
        </h1>
      </page-header>
      <main class="landing-main">
        <picture class="landing-car-image">
          <source
            :srcset="`${config.imagesPath}/main-image-light.png`"
            media="(prefers-color-scheme: dark)"
          />
          <img :src="`${config.imagesPath}/main-image.png`" alt="" />
        </picture>

        <template v-if="clickedNext">
          <h1 class="landing-main__title">
            {{ $t("landingPage.greetings.title") }}
            <br />
            {{ $t("landingPage.greetings.subTitle") }}
          </h1>
          <p class="text text-center">
            {{ $t("landingPage.greetings.shouldUseApp") }}
          </p>
          <a v-if="isMobile" class="block" :href="appDownloadLink">
            <img
              v-if="isIOS"
              class="landing-main__store-btn"
              :src="appleStoreImage"
              alt=""
            />
            <img
              v-if="isAndroid"
              class="landing-main__store-btn"
              src="@/assets/images/download-play-store.svg"
              alt=""
            />
          </a>
        </template>
        <template v-else>
          <loading-component v-if="!dynamicConfig" />
          <charging-prices
            v-if="dynamicConfig && dynamicConfig.isPriceInfoVisible"
            class="landing__charging-prices"
            :ac-charger-base-of-calculation="
              dynamicConfig.acChargerBaseOfCalculation
            "
            :ac-charger-price="dynamicConfig.acChargerPrice"
            :currency="dynamicConfig.currency"
            :dc-charger-base-of-calculation="
              dynamicConfig.dcChargerBaseOfCalculation
            "
            :dc-charger-price="dynamicConfig.dcChargerPrice"
            :prepay-amount="dynamicConfig.prepayAmount"
            :rapid-charger-base-of-calculation="
              dynamicConfig.rapidChargerBaseOfCalculation
            "
            :rapid-charger-price="dynamicConfig.rapidChargerPrice"
            :valid-from="dynamicConfig.validFrom"
            :hide-registration-charge="
              config.featureGuestChargingIsComissionBased
            "
            :guest-commission="dynamicConfig.guestCommission"
            :registration-charge="dynamicConfig.registrationCharge"
          />
          <language-picker />
        </template>
      </main>
    </div>

    <div class="landing__button-container">
      <button-component block primary @click="handleNextButtonClick">
        <template v-if="!clickedNext">
          {{ $t("landingPage.next") }}
        </template>
        <template v-else>
          {{ $t("landingPage.nextInsteadOfDownload") }}
        </template>
      </button-component>
    </div>

    <confirm-modal
      v-if="isConfirmModalActive"
      yes-button-color-class="btn-download"
      :main-title="$t('landingPage.confirm.appDownload.mainTitle')"
      :sub-title="$t('landingPage.confirm.appDownload.subTitle')"
      :yes-title="$t('landingPage.confirm.appDownload.yesTitle')"
      :no-title="$t('landingPage.confirm.appDownload.noTitle')"
      @confirm="confirmNext"
      @cancel="navigateToAppDownload"
    />
  </div>
</template>

<script lang="ts" setup>
import LanguagePicker from "@/components/ui/LanguagePicker.vue"
import ConfirmModal from "@/components/ui/ConfirmModal.vue"
import config from "../config/index"
import { isAndroid, isIOS, isMobile } from "mobile-device-detect"
import PageHeader from "../components/ui/PageHeader.vue"
import ButtonComponent from "../components/ui/ButtonComponent.vue"
import appleStoreImageHu from "@/assets/logos/apple_store_badge_web_generic_hu.svg"
import appleStoreImageEn from "@/assets/logos/apple_store_badge_web_generic_en.svg"
import ChargingPrices from "../components/ui/ChargingPrices.vue"
import LoadingComponent from "../components/ui/LoadingComponent.vue"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useUserStore } from "../store/user"
import { storeToRefs } from "pinia"
import { useDictionaryStore } from "../store/dictionary"

const { setSeenLandingPage } = useUserStore()
const dictionaryStore = useDictionaryStore()
const { dynamicConfig } = storeToRefs(dictionaryStore)
const router = useRouter()
const { locale } = useI18n()

const isConfirmModalActive = ref(false)
const clickedNext = ref(false)

const appDownloadLink = computed(() => {
  if (isIOS) {
    return config.links.app_ios
  }
  if (isAndroid) {
    return config.links.app_android
  }
  return ""
})

const appleStoreImage = computed(() => {
  return locale.value === "hu" ? appleStoreImageHu : appleStoreImageEn
})

const confirmNext = () => {
  isConfirmModalActive.value = false
  setSeenLandingPage(true)
  router.push("terms")
}

const next = () => {
  if (
    (config.links.app_ios.length > 0 || config.links.app_android.length > 0) &&
    isMobile
  ) {
    clickedNext.value = true
  } else {
    confirmNext()
  }
}

const nextInsteadOfDownload = () => {
  isConfirmModalActive.value = true
}

const handleNextButtonClick = () => {
  if (clickedNext.value) {
    nextInsteadOfDownload()
  } else {
    next()
  }
}

const navigateToAppDownload = () => {
  window.location.href = appDownloadLink.value
}
</script>
